import React, { useState } from 'react'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  Input,
  InputLabel,
  Stack,
  TextField,
} from '@mui/material'
import NormalText from 'common/Typography/NormalText'
import { FormikContextType, useFormikContext } from 'formik'
import OrbitSession from 'hooks/useCookies'

import { FormDetailsProps } from '../sharedDialogs/PartialRefundFormDialog'
export function PartialRefundForm({
  customerEmail,
  isRefundLoading,
  handleRefundLoading,
  customerId,
  mutatePartialRefund,
  transactionType,
  amount,
  currency
}: RefundFormProps) {
  const formik: FormikContextType<FormDetailsProps> = useFormikContext()
  const faberToken = OrbitSession.get('faberToken')
  const [sliderValue, setSliderValue] = React.useState<number>(0)
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);
  const handleConfirm = () => {
    setIsDialogOpen(false);
    handleRefundLoading();
    handleSubmit();
  };
  const maxRefundAmount = amount ?? 0
  const invoiceCurrency = currency === 'USD' ? 'USD' : 'SGD'

  // const marks = [
  //   {
  //     value: 0,
  //     label: '0',
  //   },
  //   {
  //     value: amount || 0,
  //     label: `${amount}`,
  //   },
  // ]
  // function valuetext(value: number) {
  //   return `${value}`
  // }


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (value >= 0) {
      setSliderValue(value);
      formik.setFieldValue('amount', value);
    }
  };

  // const sliderColor = sliderValue <= maxRefundAmount * 0.5 ? 'primary' : 'success'
  //check if user has an active subscription or not

  const handleSubmit = () => {

    if (transactionType.includes('ONETIMEPACK')) {
      if (mutatePartialRefund)
        mutatePartialRefund({
          faberToken,
          invoiceID: formik.values.invoiceID,
          refundReason: formik.values.refundReason,
          ticketId: formik.values.ticketId,
          userId: customerId,
          refundAmount: String(Number(formik.values.amount)),
          currency: invoiceCurrency,
          terminatePlan: formik.values.terminatePlan
        })
    } else {
      formik.handleSubmit()
    }
  }

  const labelStyle = sliderValue >= maxRefundAmount
    ? { fontSize: '1.2rem', fontWeight: 'bold', color: 'red' }
    : {}


  return (
    <Stack
      sx={{
        p: 2,
        boxShadow: 'rgba(0, 0, 0, 0.05) 0px 0px 0px 1px',
        borderRadius: '8px'
      }}
    >
      <Dialog open={isDialogOpen} onClose={closeDialog}>
        <DialogTitle>Confirm Partial Refund</DialogTitle>
        <DialogContent>
          <NormalText>Are you sure you want to confirm this partial refund transaction?</NormalText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleConfirm} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ py: 2 }}>
        <InputLabel shrink sx={labelStyle}>
          Maximum Refund Amount: {maxRefundAmount} {currency}
        </InputLabel>
        <TextField
          name="amount"
          placeholder="Input amount to refund"
          value={sliderValue || ''}
          onChange={handleInputChange}
          type="number"
          fullWidth
          variant="outlined"
          inputProps={{ min: 0 }}
          sx={{ mt: 2 }}
        />
      </Box>
      <Box sx={{ py: 2 }}>
        <FormControl disabled variant='standard' sx={{ width: '90%' }}>
          <InputLabel>Customer Email</InputLabel>
          <Input
            fullWidth
            multiline
            sx={{ mt: 2 }}
            id='component-disabled'
            defaultValue={customerEmail}
          />
        </FormControl>
      </Box>
      <Box sx={{ py: 2 }}>
        <FormControl variant='standard' sx={{ width: '90%' }}>
          <InputLabel shrink htmlFor='bootstrap-input'>
            Zendesk Ticket ID*
          </InputLabel>
          <TextField
            name='ticketId'
            onChange={(e) => {
              formik.handleBlur(e)
              formik.handleChange(e)
            }}
            value={formik.values.ticketId}
            autoFocus
            label=''
            id='name'
            type='text'
            fullWidth
            variant='standard'
            required
            sx={{ mt: 2 }}
            error={formik.touched.ticketId && Boolean(formik.errors.ticketId)}
            helperText={formik.touched.ticketId && formik.errors.ticketId}
          />
        </FormControl>
      </Box>

      <Box sx={{ py: 2 }}>
        <InputLabel shrink htmlFor='bootstrap-input'>
          Refund Reasons*
        </InputLabel>
        <FormControl variant='standard' sx={{ width: '90%' }}>
          <TextField
            name='refundReason'
            value={formik.values.refundReason}
            multiline
            autoFocus
            required
            onChange={(e) => {
              formik.handleBlur(e)
              formik.handleChange(e)
            }}
            error={
              formik.touched.refundReason && Boolean(formik.errors.refundReason)
            }
            helperText={
              formik.touched.refundReason && formik.errors.refundReason
            }
          />
        </FormControl>
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, mt: 2 }}>
          <NormalText theme='warning' sx={{ fontStyle: 'italic', textAlign: 'center', fontSize: 12, }}>
            {`Please terminate the packs manually (if needed) in the customer's profile after refund has been made. Refund receipt will also be sent to customer automatically upon refund`}
          </NormalText>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, mt: 2 }}>
          <NormalText sx={{ fontStyle: 'italic', textAlign: 'center', fontSize: 12, }}>
            For payment types of CHECKOUT_SESSION_JETPACKS, this partial refund process will serve as the updated refund workflow.
          </NormalText>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, mt: 2 }}>
          <LoadingButton
            loading={isRefundLoading}
            loadingIndicator={
              <CircularProgress size={24} sx={{ color: 'white' }} />
            }
            variant='contained'
            sx={{
              '&.Mui-disabled': {
                border: 'none'
              }
            }}
            disabled={!formik.isValid}
            type='submit'
            onClick={openDialog}
          >
            Submit
          </LoadingButton>
        </Box>
      </Box>
    </Stack>
  )
}

interface RefundFormProps {
  customerEmail: string
  isRefundLoading: boolean
  amount?: number
  currency?: string
  handleRefundLoading: () => void
  customerId: string
  totalOtpackDataUsed?: number
  transactionType: string
  mutatePartialRefund?: ({
    faberToken,
    invoiceID,
    refundReason,
    userId,
    ticketId,
    refundAmount,
    currency,
    terminatePlan
  }) => void
}
